import { Currency } from '@sequencehq/api'
import { DeferredRevenueTableGroupBy } from './DeferredRevenueDashboardTable'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  ActiveFilter,
  MagicTableFilterConfig,
  useMagicTableInfiniteQuery
} from '@sequencehq/core-components'
import { DeferredRevenueScheduleRecognitionMethod } from 'RevenueRecognition/revenueRecognition.types'
import { calculateProgressPercentages } from '../common/progressPercentages'

type UseDeferredRevenueDashboardTableQueryParams = {
  groupBy?: DeferredRevenueTableGroupBy
  filtersConfig: MagicTableFilterConfig<any>[]
  activeFilters: ActiveFilter[]
  asOfDate: string
}

export type DeferredRevenueSchedule = {
  id: string
  customer: {
    id: string
    name: string
  }
  product: {
    id: string
    name: string
  }
  invoice?: {
    id: string
    name: string
  }
  method: DeferredRevenueScheduleRecognitionMethod
  term?: {
    start: Date
    endInclusive: Date
  }
  progress: {
    currency: Currency
    recognized: {
      percentage: number
      amount: number
    }
    remaining: {
      percentage: number
      amount: number
    }
  }
}

export const useDeferredRevenueDashboardTableQuery = ({
  groupBy,
  filtersConfig,
  activeFilters,
  asOfDate
}: UseDeferredRevenueDashboardTableQueryParams) => {
  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboardv99990101Client.getDeferredRevenueSchedulesPaginated,
    filtersConfig,
    activeFilters,
    null,
    100,
    undefined,
    {
      ...(groupBy && { groupBy }),
      asOfDate
    }
  )

  // Transform the data to match the expected DeferredRevenueSchedule format
  const transformedInfiniteQuery = {
    ...infiniteQuery,
    data: infiniteQuery.data
      ? {
          ...infiniteQuery.data,
          pages: infiniteQuery.data.pages.map((page: any) => ({
            ...page,
            items: page.items.map((item: any): DeferredRevenueSchedule => {
              const recognizedAmount = parseFloat(item.progress.recognized)
              const remainingAmount = parseFloat(item.progress.remaining)
              const originalAmount = parseFloat(item.progress.original)
              const percentages = calculateProgressPercentages({
                original: originalAmount,
                recognized: recognizedAmount,
                remaining: remainingAmount
              })

              return {
                id: item.id,
                customer: {
                  id: item.customer.id,
                  name: item.customer.name
                },
                product: item.product,
                invoice: item.invoice,
                method: item.recognitionMethod,
                progress: {
                  currency: item.progress.currency,
                  recognized: {
                    percentage: percentages.recognized,
                    amount: recognizedAmount
                  },
                  remaining: {
                    percentage: percentages.remaining,
                    amount: remainingAmount
                  }
                }
              }
            })
          }))
        }
      : undefined
  }

  return { infiniteQuery: transformedInfiniteQuery }
}
